import { z } from 'zod';
import { MemoryEntryPointSchema } from '@terra/shared-contracts-memory';
import {
  MemoryArchiveReasonSchema,
  MemoryCallerGroupSchema,
  MemoryChangeTypeSchema,
  MemoryCockpitRangeSchema,
  MemoryEvidenceKindSchema,
  MemoryHeadDtoSchema,
  MemoryItemStatusSchema,
  MemoryObjectIdSchema,
  MemoryRetrievalSchema,
} from './memory-overview.schemas';

export const MemoryMergeDecisionSchema = z.enum([
  'exact_hash',
  'equivalent',
  'refines',
  'contradicts',
]);

export const MemoryVersionDtoSchema = z.object({
  version: z.int().positive(),
  changeType: MemoryChangeTypeSchema,
  content: z.string(),
  previousContent: z.string().nullable(),
  incomingText: z.string().nullable(),
  status: MemoryItemStatusSchema,
  entryPoint: MemoryEntryPointSchema,
  actorUserId: z.string().nullable(),
  promptHash: z.string().nullable(),
  mergeDecision: MemoryMergeDecisionSchema.nullable(),
  mergeGroupId: MemoryObjectIdSchema.nullable(),
  absorbedMemoryIds: MemoryObjectIdSchema.array(),
  overflowMemoryIds: MemoryObjectIdSchema.array(),
  skippedMemoryIds: MemoryObjectIdSchema.array(),
  mergeScores: z
    .object({
      memoryId: MemoryObjectIdSchema,
      score: z.number(),
      decision: z.string(),
      reason: z.string(),
    })
    .array(),
  evidenceMoves: z
    .object({
      evidenceId: MemoryObjectIdSchema,
      fromMemoryId: MemoryObjectIdSchema,
      alreadyOnSurvivor: z.boolean(),
    })
    .array(),
  introducedEvidenceIds: MemoryObjectIdSchema.array(),
  archiveReason: MemoryArchiveReasonSchema.nullable(),
  archiveEvidenceId: MemoryObjectIdSchema.nullable(),
  historyId: z.string().nullable(),
  fromEndpointId: z.string().nullable(),
  toEndpointId: z.string().nullable(),
  createdAt: z.iso.datetime(),
});

export const MemoryMergeChainSchema = z.object({
  hops: z
    .object({
      memoryId: MemoryObjectIdSchema,
      content: z.string(),
      version: z.int().positive(),
      status: MemoryItemStatusSchema,
    })
    .array(),
  truncated: z.boolean(),
});

export const MemoryRewireSiblingSchema = z.object({
  memoryId: MemoryObjectIdSchema,
  version: z.int().positive(),
  historyId: z.string(),
  fromEndpointId: z.string().nullable(),
  toEndpointId: z.string().nullable(),
});

export const MemoryEvidenceRowSchema = z.object({
  evidenceId: MemoryObjectIdSchema,
  kind: MemoryEvidenceKindSchema,
  role: z.enum(['input', 'derived_from']),
  introducedAtVersion: z.int().positive(),
  capturedAt: z.iso.datetime(),
  sizeBytes: z.int().nonnegative(),
  contentType: z.string(),
  contentHash: z.string(),
  inline: z.boolean(),
  locator: z.record(z.string(), z.string()),
});

export const MemoryUsagePointSchema = z.object({
  day: z.iso.date(),
  runKind: z.string(),
  callerGroup: MemoryCallerGroupSchema,
  version: z.int().positive(),
  reads: z.int().nonnegative(),
});

export const MemoryRecordSchema = z.object({
  head: MemoryHeadDtoSchema.nullable(),
  versions: MemoryVersionDtoSchema.array(),
  mergeChain: MemoryMergeChainSchema,
  absorbed: MemoryHeadDtoSchema.array(),
  rewireSiblings: MemoryRewireSiblingSchema.array(),
  evidence: MemoryEvidenceRowSchema.array(),
  usage: MemoryUsagePointSchema.array(),
});

export const GetMemoryRecordQuerySchema = MemoryCockpitRangeSchema;

export const MemoryReadRowSchema = z.object({
  id: MemoryObjectIdSchema,
  createdAt: z.iso.datetime(),
  runKind: z.string(),
  callerGroup: MemoryCallerGroupSchema,
  runId: z.string().nullable(),
  executionId: z.string().nullable(),
  retrieval: MemoryRetrievalSchema,
  query: z.string().nullable(),
  version: z.int().positive(),
  rank: z.int().nonnegative(),
  score: z.number().nullable(),
  truncated: z.boolean(),
});

export const ListMemoryReadsQuerySchema = MemoryCockpitRangeSchema.and(
  z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).prefault(50),
  }),
);

export const ListMemoryReadsResponseSchema = z.object({
  items: MemoryReadRowSchema.array(),
  nextCursor: z.string().nullable(),
});

export const MemoryEvidenceBodySchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('ok'),
    contentType: z.string(),
    text: z.string().nullable(),
    imageBase64: z.string().nullable(),
    truncated: z.boolean(),
    downloadUrl: z.string().nullable(),
  }),
  z.object({ status: z.literal('not_found') }),
  z.object({ status: z.literal('missing_object') }),
]);

export const GetMemoryRecordPayloadSchema = MemoryCockpitRangeSchema.and(
  z.object({ memoryId: MemoryObjectIdSchema }),
);

export const ListMemoryReadsPayloadSchema = ListMemoryReadsQuerySchema.and(
  z.object({ memoryId: MemoryObjectIdSchema }),
);

export const GetMemoryEvidenceBodyPayloadSchema = z.object({
  evidenceId: MemoryObjectIdSchema,
});

export type MemoryVersionDto = z.infer<typeof MemoryVersionDtoSchema>;
export type MemoryMergeChain = z.infer<typeof MemoryMergeChainSchema>;
export type MemoryRewireSibling = z.infer<typeof MemoryRewireSiblingSchema>;
export type MemoryEvidenceRow = z.infer<typeof MemoryEvidenceRowSchema>;
export type MemoryUsagePoint = z.infer<typeof MemoryUsagePointSchema>;
export type MemoryRecord = z.infer<typeof MemoryRecordSchema>;
export type MemoryReadRow = z.infer<typeof MemoryReadRowSchema>;
export type ListMemoryReadsResponse = z.infer<
  typeof ListMemoryReadsResponseSchema
>;
export type MemoryEvidenceBody = z.infer<typeof MemoryEvidenceBodySchema>;
export type GetMemoryRecordPayload = z.infer<
  typeof GetMemoryRecordPayloadSchema
>;
export type ListMemoryReadsPayload = z.infer<
  typeof ListMemoryReadsPayloadSchema
>;
export type GetMemoryEvidenceBodyPayload = z.infer<
  typeof GetMemoryEvidenceBodyPayloadSchema
>;
